"use client";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { TopNavigation } from "@/components/TopNavigation";
import { PropertyMapSection, PropertyMapSectionHandle } from "@/components/PropertyMapSection";
import { IPropertyResult, IPlace } from "@/interfaces/property";
import { PlaceDetailsCard } from "@/components/PlacesComponents/PlaceDetailsCard";
import PropertyContainer from "./PropertyContainer";
import { calculateZoomForRadius } from "@/lib/utils";

interface PropertyPageLayoutProps {
  detailsContent: React.ReactNode;
  property: IPropertyResult;
  placeType?: string;
  onPlaceTypeChange?: (placeType: string) => void;
  profileSlug?: string;
}

const PropertyPageLayout: React.FC<PropertyPageLayoutProps> = ({
  detailsContent,
  property,
  profileSlug,
}) => {
  const [selectedFilter, setSelectedFilter] = useState<string>("all");
  const [placeType, setPlaceType] = useState<string>("all");
  const [selectedPlace, setSelectedPlace] = useState<IPlace | null>(null);
  const [hoveredPlaceId, setHoveredPlaceId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("property");
  const mapRef = useRef<PropertyMapSectionHandle>(null);
  const detailsCardRef = useRef<HTMLElement | null>(null);
  const detailsContainerRef = useRef<HTMLDivElement | null>(null);

  // Calculate zoom level for 300km radius around the property
  // This ensures the map shows a 300km radius area centered on the property
  const mapZoomLevel = useMemo(() => {
    const zoom = calculateZoomForRadius(
      property.coordinates.lat,
      property.coordinates.lon,
      300, // 300km radius
      5,   // min zoom
      16   // max zoom
    );
    

    
    return zoom;
  }, [property.coordinates.lat, property.coordinates.lon]);

  useEffect(() => {
    if (selectedPlace && detailsContainerRef.current) {
      if (window.innerWidth >= 768) { // md breakpoint
        detailsContainerRef.current.scrollTop = 0;
      }
    }
  }, [selectedPlace]);

  useEffect(() => {
    if (activeTab === "property" && mapRef.current) {
      mapRef.current.flyToProperty();
    }
  }, [activeTab]);

  // You may need to get the places list from somewhere accessible here
  // For now, assume you can look up by id if needed

  const handlePlaceTypeChange = (newPlaceType: string) => {
    setPlaceType(newPlaceType);
    setSelectedFilter(newPlaceType); // keep filter in sync if needed
  };

  const handlePlaceSelect = (place: IPlace) => {
    setSelectedPlace(place);
    setHoveredPlaceId(null); // Clear hover state on selection
    setActiveTab("places");
    
    const lat = place.location?.location?.lat;
    const lon = place.location?.location?.lon;
    if (lat && lon && mapRef.current) {
      const bounds = mapRef.current.getMapBounds?.();
      if (bounds) {
        const inBounds =
          lat <= bounds.north &&
          lat >= bounds.south &&
          lon <= bounds.east &&
          lon >= bounds.west;
        if (!inBounds) {
          mapRef.current.flyToPlace(lat, lon);
        }
      }
    }
  };

  const handleDetailsClose = () => {
    setSelectedPlace(null);
    setHoveredPlaceId(null);
    setActiveTab("places");
  };

  return (
    <>
      <TopNavigation languages={[]} profileId={profileSlug ?? property.profile.id} />

      <main id="page_main" role="main" className="relative w-full min-w-small">
        <div id="wrapper_flexible_layout" className="flex flex-col md:flex-row w-full md:h-screen">

          {/* Details Panel: left on desktop, below map on mobile */}
          <div id="BeforeDetailsContainer" className="flex flex-col items-left w-full pointer-events-none md:w-small md:h-full">
            <div id="DesktopSpacer" className="w-full !h-header-desktop !min-h-header-desktop hidden md:block box-border"></div>

            <div id="DetailsContainer" ref={detailsContainerRef} className="z-10 flex flex-col items-center w-full pointer-events-none pt-[240px]
                md:w-small md:pt-[0px] md:h-full md:overflow-y-scroll scrollbar-gutter-stable"
                style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 100px)" }}>
              <section id="DetailsSection" role="region" aria-label="Details" 
              className="w-full rounded-lg md:rounded-none pointer-events-auto bg-background pt-[16px] md:pt-[24px] px-[8px] md:px-[24px]">
                <main className="flex flex-col items-center mx-auto w-full gap-[16px] md:gap-[24px]">
                  {selectedPlace ? (
                    <PlaceDetailsCard key={selectedPlace.id} place={selectedPlace} onClose={handleDetailsClose} />
                  ) : (
                    React.cloneElement(detailsContent as React.ReactElement, {
                      onPlaceTypeChange: handlePlaceTypeChange,
                      onPlaceSelect: handlePlaceSelect,
                      onPlaceHover: setHoveredPlaceId,
                      activeTab,
                      setActiveTab,
                      selectedFilter,
                      setSelectedFilter,
                      placeType,
                    })
                  )}
                </main>
              </section>
            </div>
          </div>

          {/* Map Panel: right on desktop, top on mobile */}
          <PropertyMapSection 
            ref={mapRef}
            propertyId={property.uid}
            centerLatitude={property.coordinates.lat} 
            centerLongitude={property.coordinates.lon}
            zoomLevel={mapZoomLevel} 
            mapHeight="map-small"
            placeType={placeType}
            onPlaceSelect={handlePlaceSelect}
            onPlaceHover={setHoveredPlaceId}
            selectedPlaceId={selectedPlace?.id || null}
            hoveredPlaceId={hoveredPlaceId}
          />
        </div>
      </main>
    </>
  );
};

export default PropertyPageLayout; 